/* eslint-disable max-len */
import React, { useState } from 'react';
import { shape, func } from 'prop-types';
import LaundryContainerTitle from './LaundryContainerTitle';
import { CustomProductPod } from './CustomProductPod';
import { InfoDrawer } from './InfoDrawer';
import { DRYER } from '../constants/index';
import LaundryProductsLayout from './LaundryProductsLayout';
import LaundryAddIcon from './LaundryAddIcon';

const COMMON_PRODUCT_CLASSES = 'sui-w-full md:sui-col-span-3 lg:sui-col-span-2';

/**
 * WasherDryerContainer component renders a container for displaying a washer and dryer set.
 * It includes functionality for swapping product variants and displaying additional information
 * through a drawer component.
 *
 * @param {Object} props - The props object.
 * @param {Object} props.washer - The washer product object to display.
 * @param {Object} props.dryer - The dryer product object to display.
 * @param {Function} props.onSwapVariant - Callback function to handle swapping product variants.
 *
 * @returns {JSX.Element} The rendered WasherDryerContainer component.
 */
const WasherDryerContainer = ({
  washer,
  dryer,
  onSwapVariant: handleSwapVariant
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleDrawer = (isOpen) => {
    setIsDrawerOpen(isOpen);
  };

  return (
    <>
      <div data-testid="washer-dryer-container" className="sui-border-solid sui-border-1 sui-border-primary sui-rounded-base sui-overflow-hidden sui-h-full">
        <div className="sui-flex sui-bg-subtle sui-justify-center sui-p-1">
          <LaundryContainerTitle text="Washer & Dryer Set" onInfoClick={() => handleDrawer(true)} />
        </div>
        <LaundryProductsLayout
          className="sui-gap-6 sui-px-2 sui-py-4 md:sui-px-0 lg:sui-px-0"
          grid={{
            md: 7,
            lg: 5
          }}
        >
          {
            washer && (
              <div data-testid="washer-product" className={`${COMMON_PRODUCT_CLASSES} md:sui-ml-4 lg:sui-ml-6`}>
                <CustomProductPod product={washer} onSwapVariant={handleSwapVariant} />
              </div>
            )
          }
          {
            washer && dryer && (
              <LaundryAddIcon
                grid={{
                  md: 7,
                  lg: 5
                }}
                className="sui-top-4"
                iconClassName="md:sui-col-start-4 md:sui-h-[calc(300%_+_32px)] lg:sui-col-start-3 lg:sui-h-[calc(200%_+_32px)]"
              />
            )
          }
          {
            dryer && (
              <div data-testid="dryer-product" className={`${COMMON_PRODUCT_CLASSES} md:sui-col-start-5 md:-sui-ml-4 lg:-sui-ml-6 lg:sui-col-start-4`}>
                <CustomProductPod product={dryer} onSwapVariant={handleSwapVariant} />
              </div>
            )
          }
        </LaundryProductsLayout>
      </div>
      <InfoDrawer type={DRYER} isOpen={isDrawerOpen} onClose={() => handleDrawer(false)} />
    </>
  );
};

WasherDryerContainer.propTypes = {
  washer: shape({}),
  dryer: shape({}),
  onSwapVariant: func.isRequired
};

WasherDryerContainer.defaultProps = {
  washer: null,
  dryer: null
};

WasherDryerContainer.dataModel = CustomProductPod.dataModel;

export default WasherDryerContainer;